import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Segment,
  Tab,
  TabPane,
  Header,
  Grid,
  GridColumn,
  Table,
  Dropdown,
  Confirm,
  Icon,
  Popup,
} from "semantic-ui-react";
//import { Line } from "@ant-design/charts";
import { Pie, Column, DualAxes } from "@ant-design/plots";
import {
  Permission,
  Effluentform,
  Form,
  Productionform,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./EffluentKPIsDashBoard.scss";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { formatDateView } from "../../../../utils/formatDate";
import { apiRetry } from "../../../../utils";
import EffluentSummary from "./effluentSummary";
import TableGraf from "./TableGraf";
import KPIExportIconButton from "../KPIExportIconButton";
import { drawEffluentCustomCharts } from "./pdfEffluentCharts";
import {
  buildEffluentComparisonIndustrialData,
  buildEffluentIntensityIndustrialData,
  buildEffluentComparisonDomesticData,
  buildEffluentIntensityDomesticData,
  buildSharedYScale,
} from "./effluentChartData";

import { CollapsibleSection, LoadingSpinner } from "../../../Shared";
import { Link } from "react-router-dom";
//import { Renderer as SVGRenderer } from '@antv/g-svg';

import {
  effluentKPIsCodes,
  effluentFormulasIndicators,
  determineEffluentUnits,
  effluentCodes,
  convertUnitsFinal,
} from "../../../../utils/dataAndFormulas/effluentForms";
import { formatWhole, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";

import {
  formatValue,
  convertUnits,
} from "../../../../utils/dataAndFormulas/convert";

import { normalizeUnit } from "../../../../utils/normalizeUnit";

const permissionController = new Permission();
const effluentController = new Effluentform();
const formController = new Form();
const productionController = new Productionform();

export function EffluentKPIsDashBoard(props) {
  const { site, sharedProductionForms, sharedWorkersByPeriod, siteName = "" } = props;
  const {
    user: { role },
    accessToken,
  } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  const [permissionActive, setPermissionsActive] = useState([]);
  const [baseline, setbaseline] = useState("");
  const [valueBaseline, setValueBaseline] = useState({});
  const [missingBaselineYears, setMissingBaselineYears] = useState([]);
  const [indicatorsLineBase, setIndicatorsLineBase] = useState([]);

  const [data, setData] = useState([]);
  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [configFormData, setConfigFormData] = useState(null);

  const [workersTotals, setWorkersTotals] = useState([]);

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const [lastYears, setLastYears] = useState({}); // resultados calculados
  const [totalesByFilters, setTotalesByFilters] = useState({}); // resultados calculados
  const [totalesProductionsByFilters, setTotalesProductionsByFilters] = useState({}); // resultados de producción por mes
  const [totalesWorkersByFilters, setTotalesWorkersByFilters] = useState({}); // resultados de trabajadores por mes

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));

  //declaración estado de filtros
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getMyPermissions(accessToken, true);
          setPermissionsActive(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsActive([]);
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    if (!site) return;

    const fetchData = async () => {
      startLoading();
      setData([]);
      setbaseline("");
       try {
        const currentYear = new Date().getFullYear();
        const yearsLast = Array.from({ length: 5 }, (_, i) =>
          String(currentYear - i)
        );

        // 🚀 Paralelizar consultas de baseline y datos iniciales
        const [prueba, responseBaseline] = await Promise.all([
          apiRetry(() => effluentController.getEffluentFormsByLineBaseYears(accessToken, site)),
          apiRetry(() => formController.getLastBaselineByFormBySite(accessToken, site, "production")),
        ]);

        setIndicatorsLineBase(prueba.effluentForms.indicators);

        const yearsLineaBase = prueba.effluentForms.years;
        const yearsCombined = [...new Set([...yearsLineaBase, ...yearsLast])];

        const responseEffluent = await apiRetry(() => effluentController.getEffluentFormsByLastYears(accessToken, site, yearsCombined));

        // Comentado: consulta redundante - el total de trabajadores se calcula desde datos por período
        // const totalWorkersByYear = await socialController.getTotalWorkersByYears(
        //   accessToken,
        //   site,
        //   yearsCombined
        // );

        setData(responseEffluent.effluentForms);
        // Comentado: se sobrescribe en el siguiente useEffect con el promedio calculado desde datos mensuales
        // setWorkersTotals(totalWorkersByYear.totalWorkers);

        // Procesar baseline
        if (responseBaseline.code === 200) {
          setbaseline(responseBaseline.baseline);

          const result = await Promise.all(
            Object.entries(responseBaseline.baseline)
              .filter(([anio, valor]) => valor !== null)
              .map(async ([anio, valor]) => {
                const a = await getValueBaseLine(anio, valor);
                return { [anio]: a };
              })
          );

          const resultadoFinal = result.reduce(
            (acc, obj) => ({ ...acc, ...obj }),
            {}
          );
          setValueBaseline(resultadoFinal);
        }
      } catch (error) {
        // console.error("Error loading effluent data:", error);
        setData([]);
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [site]);
// console.log('🔴 Estado totalesWorkersByFilters:', totalesWorkersByFilters);
// console.log('🔵 Estado workersTotals:', workersTotals);
// console.log('🟡 Estado valueBaseline (valores numéricos):', valueBaseline);
// console.log('🟠 Estado baseline (tipo VPS/VFS por año):', baseline);

  // Obtener configuración de campos activos del formulario
  useEffect(() => {
    (async () => {
      try {
        if (site) {
          const response = await formController.getFormsBySite(
            accessToken,
            site,
            "effluent",
            true
          );
          if (Array.isArray(response) && response.length > 0) {
            setConfigFormData(response[0]);
          } else {
            setConfigFormData(null);
          }
        }
      } catch (error) {
        // console.error("Error al obtener configFormData:", error);
        setConfigFormData(null);
      }
    })();
  }, [site]);

  // Función que obtiene los campos fuente de un KPI basándose en effluentFormulasIndicators
  const getKPISourceFields = useCallback((kpiName) => {
    const kpiSourceMap = {
      'total_domestic_effluents_kpis': ['domestic_effluents'],
      'total_industrial_effluents_kpis': ['industrial_effluents'],
      'total_combined_effluents_kpis': ['combined_effluents'],
      'domestic_effluents_kpis': ['domestic_effluents'],
      'industrial_effluents_kpis': ['industrial_effluents'],
      'sludge_and_mud_kpis': ['sludge_and_mud'],
      'recycled_effluents_kpis': ['recycled_effluents'],
      'treatment_cost_domestic_effluents_kpis': ['treatment_cost_domestic_effluents'],
      'industrial_effluent_treatment_cost_kpis': ['industrial_effluent_treatment_cost'],
      'sludge_disposal_cost_kpis': ['sludge_disposal_cost'],
    };
    return kpiSourceMap[kpiName];
  }, []);

  // 🚀 Memoizar determinación de campos basándose en configFormData
  const determineFieldsKPIs = useMemo(() => {
    const fields = new Set();

    Object.keys(effluentKPIsCodes).forEach((key) => {
      const sourceFields = getKPISourceFields(key);

      // Si no tiene campos fuente definidos, agregarlo por defecto
      if (!sourceFields) {
        fields.add(key);
        return;
      }

      // Si hay configFormData, verificar si TODOS los campos fuente están activos
      if (configFormData?.fields && configFormData.fields.length > 0) {
        const allSourceFieldsActive = sourceFields.every(sourceFieldName => {
          const fieldConfig = configFormData.fields.find(f => f.name === sourceFieldName);
          return !fieldConfig || fieldConfig.active !== false;
        });

        if (allSourceFieldsActive) {
          fields.add(key);
        }
      } else {
        // Si no hay configFormData, mostrar todos los campos (comportamiento por defecto)
        fields.add(key);
      }
    });

    return Array.from(fields);
  }, [configFormData, getKPISourceFields]);

  // 🚀 Memoizar campos de códigos
  const determineFieldsCode = useMemo(() => {
    const fields = new Set();

    Object.keys(effluentCodes).forEach((key) => {
      fields.add(key);
    });

    return Array.from(fields);
  }, []);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        if (data && valueBaseline && sharedWorkersByPeriod && sharedProductionForms) {
          // Detectar años sin baseline para mostrar aviso
          const currentYear = new Date().getFullYear();
          const yearsToCheck = [0,1,2,3,4].map(i => String(currentYear - i));
          setMissingBaselineYears(yearsToCheck.filter(y => !valueBaseline?.[y]));

          // 🔹 Últimos 5 años dinámicos
          const lastFiveYears = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ];

          // 🔹 Usar trabajadores compartidos desde DashBoard.js
          setTotalesWorkersByFilters(sharedWorkersByPeriod);

          // 🔹 Calcular promedio de trabajadores por año desde datos mensuales
          const avgWorkersByYear = {};
          const workersByPeriod = sharedWorkersByPeriod || {};
          for (const year in workersByPeriod) {
            const months = Object.values(workersByPeriod[year]);
            if (months.length > 0) {
              const sum = months.reduce((a, b) => a + b, 0);
              avgWorkersByYear[year] = Math.round(sum / months.length); // Promedio mensual
            } else {
              avgWorkersByYear[year] = 0;
            }
          }
          // console.log('📈 Promedio trabajadores por año (desde meses):', avgWorkersByYear);
          setWorkersTotals(avgWorkersByYear); // Actualizar workersTotals con el promedio calculado

          const previous_totals = await transformData1(data); //separa por años los formularios
          calculateDataFinal(previous_totals, avgWorkersByYear); //calcula los totales finales indicadores, pasando el promedio

          // 🔹 Filtrar datos solo de esos 5 años
          const filteredByYears = data?.data
            ?.filter((item) => lastFiveYears.includes(Number(item.year))) // 👈 convierte el string
            ?.reduce((acc, item) => {
              const year = Number(item.year);
              if (!acc[year]) acc[year] = [];
              acc[year].push(item);
              return acc;
            }, {});

          // 🔹 Guardar los últimos 5 años en estado
          setLastYears(lastFiveYears);

          const results = await getLastThreeYearsData(filteredByYears);
          setTotalesByFilters(results);

          // Calcular producción por mes usando datos compartidos desde DashBoard.js
          const totalesFinal = await calcularVPSVFSporMesPorAño(sharedProductionForms, baseline);
          setTotalesProductionsByFilters(totalesFinal);
        }
      } catch (error) {
        // console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [data, valueBaseline, sharedProductionForms, sharedWorkersByPeriod]);

  //Calcula el valor de linea base por año hay que cambiar si es por vfs o vps
  const getValueBaseLine = async (year, type) => {
    try {
      if (type === "VPS") {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_production"
          );
        //setValueBaseline(response1.total)
        return response1.total;
      } else {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_billing"
          );
        //setValueBaseline(response1.total)
        return response1.total;
      }
    } catch (error) {
      //console.error(error);
      return 1; /// Retorna uno por si hay error ver este tema
    }
  };

  //Funcion para obtener los valores de los campos por cada año
  const transformData1 = async (data) => {
    // Inicializar el objeto de totales y los conceptos
    const totalsPerYear = {};
    const conceptsSet = [];
    const unitsCache = {}; // Aquí almacenaremos los datos de unidades por año

    // Unidades configuradas en configFormData tienen prioridad máxima
    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    const fields = determineFieldsCode; // 🚀 Usar directamente el array memoizado

    // Verificar si data y data.data son válidos y contienen datos
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      //console.log("No hay datos para procesar o data.data no es un array válido");
      return {}; // Retornar un objeto vacío o manejar el caso según se requiera
    }

    // Crear un array de años únicos de los datos de consumo
    const uniqueYears = [
      ...new Set(data.data.map((item) => String(item.year))),
    ];

    // Primero, obtener las unidades para todos los años requeridos
    //console.log('Obteniendo unidades para los años:', uniqueYears);
    const promises = uniqueYears.map(async (año) => {
      try {
        // Realizar la llamada para obtener las unidades para el año
        const units = await effluentController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );

        // Guardar en el caché si las unidades son válidas
        if (units && units.fields) {
          unitsCache[año] = units;
          //console.log(`Unidades guardadas en caché para el año ${año}:`, units);
        } else {
          // console.log(`No se obtuvieron unidades válidas para el año ${año}`);
        }
      } catch (error) {
        //console.log(`Error al obtener unidades para el año ${año}:`, error);
      }
    });

    // Esperar a que todas las consultas de unidades se resuelvan
    await Promise.all(promises);

    // Ahora procesamos los datos de consumo usando las unidades en caché
    // console.log('Procesando los consumos');
    if (data.data !== undefined) {
      // Crear un array de promesas para manejar las llamadas asíncronas
      const consumptionPromises = data.data.map(async (item) => {
        const year = String(item.year);

        // Unidades del caché (puede ser undefined si getLastUnitFieldsByYear falló — se usa fallback del documento)
        const units = unitsCache[year];

        // Iterar sobre los conceptos que tenemos en el item
        fields.forEach((concept) => {
          // Si el concepto existe, procesamos
          if (item[concept]) {
            // Añadir el concepto a la lista de concepts únicos
            conceptsSet.push(concept);

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept]) {
              totalsPerYear[concept] = {};
            }

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept][year]) {
              totalsPerYear[concept][year] = {
                total: 0,
                unit: configUnits[concept] || units?.fields?.[concept] || item[concept]?.unit || "",
              };
            }

            // Sumar el valor de consumo
            totalsPerYear[concept][year].total += item[concept].value
              ? convertUnitsFinal(
                  item[concept].value,
                  item[concept].unit,
                  totalsPerYear[concept][year].unit,
                  concept
                )
              : 0;
          }
        });
      });
      // Esperar a que todas las promesas de consumo se resuelvan
      await Promise.all(consumptionPromises);
    }

    return totalsPerYear;
  };

  //Funcion para obtener los valores de los indicadores por cada año
  // avgWorkersByYearParam: parámetro opcional para pasar el promedio calculado desde datos mensuales
  const calculateDataFinal = async (totales, avgWorkersByYearParam = null) => {
    // Inicializar el objeto de totales y los conceptos

    const totalsFinal = { ...totales };
    const conceptsSet = [];
    // Usar el parámetro si se pasa, sino usar el estado workersTotals
    const workersDataToUse = avgWorkersByYearParam || workersTotals;
    // console.log(workersTotals)
    if (totales !== undefined) {
      for (let item in totales) {
        if (totales.hasOwnProperty(item)) {
          for (let year in totales[item]) {
            //const valueB = await getValueBaseLine(year, baseline[year]);
            const valueB = valueBaseline[year];

            if (totales[item].hasOwnProperty(year)) {
              const fields = determineFieldsKPIs; // 🚀 Usar directamente el array memoizado

              // Iterar sobre los conceptos que tenemos en el item
              fields.forEach((concept) => {
                // Añadir el concepto a la lista de conceptos únicos
                // Inicializar el total para el año si no existe
                if (!totalsFinal[concept]) {
                  totalsFinal[concept] = {};
                  conceptsSet.push(concept);
                }

                // Inicializar el total para el año si no existe
                if (!totalsFinal[concept][year]) {
                  totalsFinal[concept][year] = {};
                }

                totalsFinal[concept][year] = effluentFormulasIndicators(
                  concept,
                  year,
                  totalsFinal,
                  !valueB || valueB === 0 ? 1 : valueB,
                  workersDataToUse[year] // Usa promedio desde datos mensuales si se pasa
                );
              });
            }
          }
        }
      }
    }

    //setConceptos(removeNulls(totalsFinal, conceptsSet));

    setConceptos(conceptsSet);
    setTotales(totalsFinal);
  };
  // Función para obtener los valores de un año y mes específicos (period en texto)
  const getDataByYearAndMonth = async (data, year, month) => {
    const results = {};
    const fields = determineFieldsCode; // 🚀 Usar directamente el array memoizado
    const unitsCache = {};

    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    if (!data || !data || Object.keys(data).length === 0) {
      return {};
    }

    // Diccionario de meses
    const monthMap = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const yearStr = String(year);
    const monthStr =
      typeof month === "string"
        ? monthMap[month] || ""
        : String(month).padStart(2, "0");

    if (!monthStr) {
      // console.warn("Mes inválido o no reconocido:", month);
      return {};
    }

    const yearData = data[yearStr];
    if (!Array.isArray(yearData)) {
      // console.warn("No hay datos para el año:", yearStr);
      return {};
    }

    const filteredData = yearData.filter(
      (item) => monthMap[item.period] === monthStr
    );

    if (filteredData.length === 0) {
      // console.warn(`No hay datos para ${month} ${yearStr}`);
      return {};
    }

    // Procesar resultados por campo
    filteredData.forEach((item) => {
      const units = unitsCache[yearStr];

      fields.forEach((concept) => {
        if (item[concept]) {
          if (!results[concept]) {
            results[concept] = {
              total: 0,
              unit: configUnits[concept] || units?.fields[concept] || item[concept]?.unit || "",
            };
          }

          results[concept].total += item[concept].value
            ? convertUnitsFinal(
                item[concept].value,
                item[concept].unit,
                results[concept].unit,
                concept
              )
            : 0;
        }
      });
    });

    // ==========================
    // 🔹 Calcular energía total (sin incluir costos)
    // ==========================

    // Lista de conceptos energéticos (sin costos)
    const effluentConcepts = Object.keys(effluentCodes).filter(
      (key) => !key.includes("cost")
    );

    let totalEffluentMonth = 0;
    let effluentUnit = "";

    effluentConcepts.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        totalEffluentMonth += results[concept].total;
        effluentUnit = results[concept].unit || effluentUnit;
      }
    });

    results.total_effluent_month = {
      total: totalEffluentMonth,
      unit: effluentUnit || "MWh",
    };

    // ==========================
    // 🔹 Calcular renovables
    // ==========================

    // ==========================
    // 🔹 Calcular electrica
    // ==========================
    return results;
  };

  // ==========================
  // 🔹 Calcular ultimos 3 años
  // ==========================

  const getLastThreeYearsData = async (totalesByFilters) => {
    const currentYear = new Date().getFullYear();
    const lastThreeYears = [currentYear - 2, currentYear - 1, currentYear];

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const resultsByYear = {};

    for (const year of lastThreeYears) {
      resultsByYear[year] = {};

      for (const month of months) {
        const data = await getDataByYearAndMonth(totalesByFilters, year, month);
        resultsByYear[year][month] = data;
      }
    }

    return resultsByYear;
  };

  const removeNulls = (data, concepts) => {
    // Iterar sobre las claves externas
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        // Verificar si todos los valores internos son 0
        const allZero = Object.values(data[key]).every(
          (value) =>
            value.total === 0 || value.total === undefined || isNaN(value.total)
        );

        // Si todos los valores son 0, eliminar la clave externa
        if (allZero) {
          //delete concepts[key];
          concepts = concepts.filter((item) => item !== key);
        }
      }
    }
    return concepts;
  };

  async function calcularVPSVFSporMesPorAño(documentosPorAño, baselines) {

  const resultado = {};

  for (const [year, docs] of Object.entries(documentosPorAño)) {
    const baselineType = baselines[year]; // "VPS" o "VFS"
    if (!baselineType) continue;

    const isProduccion = baselineType === "VPS";
    const prefix = isProduccion ? "VP" : "VF";

    resultado[year] = {};

    for (const doc of docs) {
      const mes = doc.period;
      if (!mes) continue;

      let totalMes = 0;

      // Recorremos todos los campos del documento
      for (const [key, campo] of Object.entries(doc)) {
        // buscamos solo los campos dinámicos de producción o facturación
        if (campo?.code && campo.code.startsWith(prefix) && !campo.code.startsWith(prefix + "S")) {
          const value = Number(campo.value) || 0;
          const coef = Number(campo.standardized_coefficient) || 1;
          totalMes += value * coef;
        }
      }

      resultado[year][mes] = totalMes;
    }
  }

  return resultado;
}

  const summaryRef = useRef();
  const tableGrafRef = useRef();
  const comparisonIndustrialRef = useRef();
  const intensityIndustrialRef = useRef();
  const comparisonDomesticRef = useRef();
  const intensityDomesticRef = useRef();

  const yearsWithData = useMemo(() => {
    if (!data?.years || !conceptos || conceptos.length === 0) return [];
    return [...data.years]
      .sort((a, b) => b - a)
      .slice(0, 5)
      .filter(year =>
        conceptos.some(item => (totales[item]?.[year]?.total || 0) !== 0)
      );
  }, [data, conceptos, totales]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (
    !data ||
    !data.years ||
    data.years.length === 0 ||
    !conceptos ||
    conceptos.length === 0
  ) {
    return (
      <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
        <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
          <Icon name="exclamation triangle" size="huge" color="gray" />
        </div>
        <div
          style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}
        >
          {t("no_data_available") || "No hay datos disponibles"}
        </div>
      </div>
    );
  } else {
    const exportYears = data?.years ? [...data.years].sort((a, b) => Number(b) - Number(a)).slice(0, 5) : [];

    // Umbral de magnitud para inferir Regla 1 (entero) en la tabla del PDF
    // — mismo criterio que resolveRowRule más abajo (tabla en pantalla).
    const PDF_EFFLUENT_WHOLE_NUMBER_THRESHOLD = 100;
    const PDF_EFFLUENT_ROW_FORMAT_RULES = { domestic_effluents_kpis: "whole" };

    // AJUSTES DE MAGNITUDES NUMÉRICAS aplicado a la tabla del PDF (antes
    // usaba `toFixed(2)` fijo + coma decimal). Replica resolveRowRule /
    // formatByRowRule de la tabla en pantalla: regla explícita si existe,
    // costos -> Regla 1, el resto según magnitud (Regla 1 si es grande, si
    // no Regla 2+).
    const formatEffluentCellForPdf = (value, item) => {
      if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
      let rule = PDF_EFFLUENT_ROW_FORMAT_RULES[item];
      if (!rule) {
        if (item.includes("cost")) {
          rule = "whole";
        } else {
          const rowValues = Object.values(totales[item] || {})
            .map((entry) => entry?.total)
            .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
            .map(Math.abs);
          rule = rowValues.length > 0 && Math.max(...rowValues) >= PDF_EFFLUENT_WHOLE_NUMBER_THRESHOLD ? "whole" : "adaptive";
        }
      }
      const formatted = rule === "whole" ? formatWhole(value) : formatMagnitudeAdaptive(value);
      // autoTable dibuja esta celda con doc.text() nativo (no SVG) — el
      // espacio fino (U+202F) no existe en WinAnsi y rompe el texto (mismo
      // problema ya resuelto en GEI/Social/Residuos).
      return formatted.replace(/ /g, " ");
    };

    return (
      <div className="list-effluent-forms">

        {/* Aviso cuando hay años sin baseline VPS/VFS */}
        {missingBaselineYears.length > 0 && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
            padding: "10px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "0.92em",
          }}>
            <Icon name="warning sign" color="yellow" />
            <span>
              Algunos indicadores (KPIs) para los años <strong>{missingBaselineYears.join(", ")}</strong> mostrarán valor 0. Configurá el volumen producido (VPS) o el volumen facturado (VFS) como línea base para verlos correctamente.{" "}
              <Link to="/admin/data/productionforms" style={{ color: "#856404", fontWeight: "bold" }}>
                Configurar línea base →
              </Link>
            </span>
          </div>
        )}

        {/* Sección de Resumen */}
        <div className="effluent-summary-section" ref={summaryRef}>
          <EffluentSummary
            filters={filters}
            site={site}
            valueBaseline={valueBaseline}
            effluentController={Effluentform}
            totales={totales}
            selectorSlot={<YearMonthSelector onChange={setFilters} availableYears={yearsWithData} />}
            exportSlot={
              <KPIExportIconButton
                title={t("effluent") || "Efluentes"}
                conceptos={conceptos}
                years={exportYears}
                totales={totales}
                codes={effluentKPIsCodes}
                t={t}
                codes={effluentKPIsCodes}
                formatCellValue={formatEffluentCellForPdf}
                chartRefs={[
                  { ref: summaryRef, label: t("summary") || "Resumen" },
                ]}
                renderCustomCharts={(doc, chartCtx) => drawEffluentCustomCharts(doc, chartCtx, {
                  totales,
                  filters,
                  accessToken,
                  site,
                  resultsByYear: totalesByFilters,
                  resultsProductions: totalesProductionsByFilters,
                  resultsWorkers: totalesWorkersByFilters,
                  t,
                })}
                siteName={siteName}
              />
            }
          />
        </div>

        {/* Sección de Gráficos */}
        <div className="effluent-tablegraf-section" ref={tableGrafRef}>
          <TableGraf filters={filters} totales={totales} t={t} site={site}/>
        </div>

        {/* Sección de Comparación - Industrial */}
        <CollapsibleSection
          title={t("industrial_effluents_evolution")}
          className="effluent-comparison-section"
          ref={comparisonIndustrialRef}
        >
          <EffluentComparisonChartIndustrial resultsByYear={totalesByFilters} t={t}/>
        </CollapsibleSection>

        {/* Sección de Intensidad - Industrial */}
        <CollapsibleSection
          title={t("industrial_effluents_intensity")}
          className="effluent-intensity-section"
          ref={intensityIndustrialRef}
        >
          <EffluentMonthlyChartIndustrial resultsByYear={totalesByFilters} t={t} resultsProductions={totalesProductionsByFilters} valueBaseline={baseline}/>
        </CollapsibleSection>

        {/* Sección de Comparación - Doméstico */}
        <CollapsibleSection
          title={t("domestic_effluents_evolution")}
          className="effluent-comparison-section"
          ref={comparisonDomesticRef}
        >
          <EffluentComparisonChartDomestic resultsByYear={totalesByFilters} t={t} />
        </CollapsibleSection>

        {/* Sección de Intensidad - Doméstico */}
        <CollapsibleSection
          title={t("domestic_effluents_intensity")}
          className="effluent-intensity-section"
          ref={intensityDomesticRef}
        >
          {/* <EffluentMonthlyChartDomestic resultsByYear={totalesByFilters} t={t} resultsProductions={totalesProductionsByFilters}/> */}
          <EffluentMonthlyChartDomestic resultsByYear={totalesByFilters} t={t} resultsProductions={totalesProductionsByFilters} resultsWorkers={totalesWorkersByFilters}/>
        </CollapsibleSection>

        {/* Sección de Tabla de Datos */}
        <TableYears
          filters={filters}
          data={data}
          accessToken={accessToken}
          t={t}
          conceptos={conceptos}
          totales={totales}
          language={language}
          indicatorsLineBase={indicatorsLineBase}
          valueBaseline={valueBaseline}
          defaultOpen={true}
        />

        {/* Sección de Valores Intermedios */}
        {/* <IntermediateValuesDisplay
          resultsProductions={totalesProductionsByFilters}
          resultsWorkers={totalesWorkersByFilters}
          t={t}
        /> */}
      </div>
    );
  }
}

// 🚀 Memoizar TableYears para evitar re-renders innecesarios
const TableYears = React.memo(function TableYears(props) {
  const {
    data,
    conceptos,
    totales,
    t,
    language,
    valueBaseline,
    indicatorsLineBase,
    defaultOpen = true,
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [years, setYears] = useState([]);
  const [months, setMoths] = useState([]);
  const [listConceptos, setListConceptos] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Función helper para convertir unidades automáticamente para mejor legibilidad
  const autoConvertToLiters = (value, unit, force = false) => {
    // Convertir m³ a litros si el valor es < 1 (empieza con 0,...)
    if ((unit === "m3" && value < 1 && value > 0) || force) {
      return {
        value: value * 1000, // Convertir m³ a litros (1 m³ = 1000 L)
        unit: "L"
      };
    }

    // Convertir galones a m³ si el valor es >= 1000 (valores muy grandes)
    if (unit === "galon" && value >= 1000) {
      return {
        value: value / 264.172, // Convertir galones a m³ (1 m³ = 264.172 gal)
        unit: "m3"
      };
    }

    return { value, unit };
  };


  useEffect(() => {
    (async () => {
      try {
        const sortedYears = data.years ? data.years.sort((a, b) => b - a).slice(0, 5) : [];
        setYears(sortedYears);
      } catch (error) {
        // console.error(error);
      }
    })();
  }, [data]);

  // Buscar el primer año que tenga unidad definida
  const obtenerUnidadDeOtrosAnios = (datos, concepto) => {
    if (!datos[concepto]) return "";
    const años = Object.keys(datos[concepto]);
    const añoConUnidad = años.find((año) => datos[concepto][año]?.unit);
    return añoConUnidad ? datos[concepto][añoConUnidad].unit : "";
  };

  useEffect(() => {
    (async () => {
      try {
        // Obtener las unidades buscando el primer año con unidad definida
        let valor = {};
        conceptos.map((field) => {
          valor[field] = obtenerUnidadDeOtrosAnios(totales, field);
        });
        setSelectedUnits(valor);
      } catch (error) {
        //console.error(error);
        setSelectedUnits([]);
      }
    })();
  }, [conceptos, totales]);

  useEffect(() => {
    (async () => {
      try {
        setListConceptos(conceptos);
      } catch (error) {
        // console.error(error);
      }
    })();
  }, [conceptos]);

  // Función para obtener el icono de aumento o disminución
  const getComparativeIcon = (currentYear, previousYear) => {
    if (previousYear !== null) {
      if (currentYear > previousYear) {
        return <Icon disabled name="angle up" color="green" />; // Aumento
      } else if (currentYear < previousYear) {
        return <Icon disabled name="angle down" color="red" />; // Disminución
      } else if (currentYear === previousYear) {
        return <span style={{ color: "blue", fontWeight: "bold" }}>=</span>;
      }
    }
    return null; // Sin cambio
  };

  // Función para obtener el icono de aumento o disminución
  const getIndicators = (item) => {
    let result = indicatorsLineBase.find((value) => value.name === item);
    if (result) {
      return result?.yearBaseline || 0;
    }
    return 0; // Sin cambio
  };

  // Regla explícita ya existente: KPIefl4 (L/trabajador) siempre entero
  const ROW_FORMAT_RULES = {
    domestic_effluents_kpis: "whole",
  };

  // Umbral de magnitud para inferir Regla 1 (entero) en filas sin regla explícita
  const WHOLE_NUMBER_THRESHOLD = 100;

  // Determina la regla de la fila: usa la asignación explícita si existe; si no la
  // infiere por magnitud (costos y valores grandes -> Regla 1; el resto -> Regla 2/2+)
  const resolveRowRule = (item, values) => {
    if (ROW_FORMAT_RULES[item]) return ROW_FORMAT_RULES[item];
    if (item.includes("cost")) return "whole";
    const nonZero = values
      .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
      .map(Math.abs);
    if (nonZero.length === 0) return "adaptive";
    return Math.max(...nonZero) >= WHOLE_NUMBER_THRESHOLD ? "whole" : "adaptive";
  };

  // Formatea un valor según la regla resuelta para su fila
  const formatByRowRule = (rule, value) => {
    if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
    return rule === "whole" ? formatWhole(value) : formatMagnitudeAdaptive(value);
  };

  const renderPill = (rule, displayValue, prevDisplayValue) => {
    const numValue = displayValue != null ? Number(displayValue) : null;
    const numPrev = prevDisplayValue != null ? Number(prevDisplayValue) : null;

    if (numValue == null || numValue === 0) {
      return <span className="gei-value-pill pill-empty" />;
    }

    let pillClass;
    if (numPrev == null || numPrev === 0) {
      pillClass = "pill-neutral";
    } else if (numValue > numPrev) {
      pillClass = "pill-up";
    } else if (numValue < numPrev) {
      pillClass = "pill-down";
    } else {
      pillClass = "pill-neutral";
    }

    return (
      <span className={`gei-value-pill ${pillClass}`}>
        {formatByRowRule(rule, numValue)}
      </span>
    );
  };

  return (
    <>
      <div className="kpi-table-section">
      <div className="section-content">
      <Table celled structured size="small" className="gei-kpi-table" unstackable>
        <Table.Header>
          <Table.Row
            onClick={() => setIsOpen(!isOpen)}
            style={{ cursor: "pointer" }}
          >
            <Table.HeaderCell textAlign="center">
              {t("code")}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              {t("indicators")}
            </Table.HeaderCell>
            {years.map((year, index) => (
              <Table.HeaderCell textAlign="center" key={index}>
                {year}
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell textAlign="center">
              {t("LB")}
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{ width: "200px" }}
            >
              <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {t("units")}
                <Icon name={isOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        {isOpen && <Table.Body>
          {conceptos.filter(item => {
            // Verificar si tiene al menos un valor no cero y válido
            if (!totales[item]) return false;
            return Object.values(totales[item]).some(val => val.total && val.total !== 0 && !isNaN(val.total));
          }).map((item, index) => {
            // KPIefl4 (domestic_effluents_kpis) siempre se muestra en L/trabajador, sin importar la magnitud
            const forceL = item === "domestic_effluents_kpis";

            // Recopilar todos los valores de la fila para determinar decimales consistentes
            const rowValues = years
              .map(y => {
                const originalValue = totales[item]?.[y]?.total || 0;
                const originalUnit = totales[item]?.[y]?.unit || "";
                const { value: displayValue } = autoConvertToLiters(originalValue, originalUnit, forceL);
                return displayValue;
              })
              .filter(v => typeof v === "number" && !isNaN(v));
            const lbOriginalValue = totales[item]?.[getIndicators(item)]?.total || 0;
            const lbOriginalUnit = totales[item]?.[getIndicators(item)]?.unit || "";
            const { value: lbDisplayValue } = autoConvertToLiters(lbOriginalValue, lbOriginalUnit, forceL);
            if (typeof lbDisplayValue === "number" && !isNaN(lbDisplayValue)) rowValues.push(lbDisplayValue);

            const rowRule = resolveRowRule(item, rowValues);

            return (
            <React.Fragment key={index}>
              <Table.Row>
                <Table.Cell data-label={t("code")}>{effluentKPIsCodes[item]}</Table.Cell>
                <Table.Cell data-label={t("indicators")}>{t(item)}</Table.Cell>
                {years.map((year, i) => {
                  if (totales[item]) {
                    const originalValue = totales[item][year]?.total || 0;
                    const originalUnit = totales[item][year]?.unit || "";
                    const { value: displayValue } = autoConvertToLiters(originalValue, originalUnit, forceL);

                    const prevOriginalValue = totales[item][year - 1]?.total || 0;
                    const prevOriginalUnit = totales[item][year - 1]?.unit || "";
                    const { value: prevDisplayValue } = autoConvertToLiters(prevOriginalValue, prevOriginalUnit, forceL);

                    const pill = renderPill(rowRule, displayValue, prevDisplayValue);
                    return (
                      <Table.Cell key={i} textAlign="center" data-label={year}>
                        <Popup
                          content={`Valor real: ${parseFloat(Number(originalValue).toFixed(6))} ${originalUnit}`}
                          trigger={<span style={{ cursor: "help" }}>{pill}</span>}
                          position="top center"
                          size="tiny"
                        />
                      </Table.Cell>
                    );
                  }
                  return <Table.Cell key={i} data-label={year}>{""}</Table.Cell>;
                })}
                <Table.Cell data-label={t("LB")}>
                  <Popup
                    content={`Valor real: ${lbOriginalValue} ${lbOriginalUnit}`}
                    trigger={
                      <span style={{ cursor: "help" }}>
                        {formatByRowRule(rowRule, lbDisplayValue)}
                      </span>
                    }
                    position="top center"
                    size="tiny"
                  />
                </Table.Cell>
                <Table.Cell data-label={t("units")}>
                  {/* Unidad - Si se convirtió a litros, mostrar en naranja */}
                  {(() => {
                    const originalValue = totales[item][getIndicators(item)]?.total || 0;
                    const originalUnit = selectedUnits[item] || "";

                    const { unit: displayUnit } = autoConvertToLiters(originalValue, originalUnit, forceL);
                    const wasConverted = displayUnit !== originalUnit && originalUnit;

                    // Determinar el sufijo según el tipo de item
                    let suffix = "";
                    if (
                      item === "domestic_effluents_kpis" ||
                      item === "treatment_cost_domestic_effluents_kpis"
                    ) {
                      suffix = ` / ${t("employee")}`;
                    } else if (
                      item === "industrial_effluents_kpis" ||
                      item === "combined_effluents_kpis" ||
                      item === "sludge_and_mud_kpis" ||
                      item === "industrial_effluent_treatment_cost_kpis" ||
                      item === "recycled_effluents_kpis" ||
                      item === "sludge_disposal_cost_kpis"
                    ) {
                      suffix = ` / ${t("unit")}`;
                    }

                    // Si no hay unidad, mostrar "-"
                    if (!originalUnit && !displayUnit) {
                      return "-";
                    }

                    // Si se convirtió a litros, mostrar en naranja con sufijo
                    if (wasConverted) {
                      return <span style={{ color: "#000", fontWeight: "bold" }}>{normalizeUnit(displayUnit)}{suffix}</span>;
                    }

                    return `${normalizeUnit(originalUnit)}${suffix}` || "-";
                  })()}
                  {/* <Dropdown
                      onChange={(e, data) => {
                        handleUnitChange(item, data);
                      }}
                      options={determineEffluentUnits(item).map((ds) => {
                        return {
                          key: ds.key,
                          text: ds.name,
                          value: ds.value,
                        };
                      })}
                      search
                      inline
                      defaultValue={determineEffluentUnits(item)[0].value}
                    /> */}
                </Table.Cell>

                {/* <Table.Cell> */}
                  {/* {" "}
                    {convertUnitsFinal(
                      totales[item][getIndicators(item)]?.total || 0,
                      totales[item][getIndicators(item)]?.unit,
                      "MWh",
                      item
                    ) || 0} */}
                {/* </Table.Cell> */}
              </Table.Row>
            </React.Fragment>
            );
          })}
        </Table.Body>}
      </Table>
      </div>
      </div>
    </>
  );
});

// 🚀 Memoizar YearMonthSelector
export const YearMonthSelector = React.memo(({ onChange, availableYears = [] }) => {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const years = useMemo(() => availableYears.length > 0
    ? [...availableYears].sort((a, b) => b - a).map(y => ({ key: y, text: y, value: y }))
    : [
        { key: currentYear, text: currentYear, value: currentYear },
        { key: currentYear - 1, text: currentYear - 1, value: currentYear - 1 },
        { key: currentYear - 2, text: currentYear - 2, value: currentYear - 2 },
        { key: currentYear - 3, text: currentYear - 3, value: currentYear - 3 },
        { key: currentYear - 4, text: currentYear - 4, value: currentYear - 4 },
      ],
  [currentYear, availableYears]);

  const months = useMemo(() => [
    { key: 1, text: "Enero", value: 1 },
    { key: 2, text: "Febrero", value: 2 },
    { key: 3, text: "Marzo", value: 3 },
    { key: 4, text: "Abril", value: 4 },
    { key: 5, text: "Mayo", value: 5 },
    { key: 6, text: "Junio", value: 6 },
    { key: 7, text: "Julio", value: 7 },
    { key: 8, text: "Agosto", value: 8 },
    { key: 9, text: "Septiembre", value: 9 },
    { key: 10, text: "Octubre", value: 10 },
    { key: 11, text: "Noviembre", value: 11 },
    { key: 12, text: "Diciembre", value: 12 },
  ], []);

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12

  useEffect(() => {
    if (availableYears && availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      const sorted = [...availableYears].sort((a, b) => b - a);
      const newYear = sorted[0];
      setSelectedYear(newYear);
      onChange({ year: newYear, month: selectedMonth });
    }
  }, [availableYears]);

  const handleYearChange = useCallback((e, { value }) => {
    setSelectedYear(value);
    onChange({ year: value, month: selectedMonth });
  }, [onChange, selectedMonth]);

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
      <Dropdown
        placeholder="Año"
        selection
        options={years}
        value={selectedYear}
        onChange={handleYearChange}
        className="year-filter-dropdown"
      />
    </div>
  );
});

function EffluentComparisonChartIndustrial({ resultsByYear , t}) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // effluentChartData.js para poder reusarla también desde el export a PDF
  // (pdfEffluentCharts.js).
  const { years, barData, lineData, yearColorMap } = useMemo(
    () => buildEffluentComparisonIndustrialData(resultsByYear, t),
    [resultsByYear, t]
  );

  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(years);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredBarData = useMemo(() =>
    barData.filter(d => activeYears.includes(String(d.year))),
  [barData, activeYears]);

  const filteredLineData = useMemo(() =>
    lineData.filter(d => activeYears.includes(String(d.year))),
  [lineData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredBarData, filteredLineData);

  const config = {
    xField: "month",
    legend: false,
        autoFit: true,
  padding: 'auto', // Deja que la librería calcule el espacio
  appendPadding: [20, 0, 20, 0], // [arriba, derecha, abajo, izquierda]
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    tooltip: {
      items: [
        (item) => ({
          name: item.type,
          value: formatWhole(item.value),
        }),
      ],
    },
    children: [
      {
        data: filteredBarData,
        type: "interval",
        yField: "value",
        colorField: "year",
        seriesField: "year",
        group: true,
        style: { maxWidth: 40 },
        scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
        interaction: { elementHighlight: { background: true } },
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year",
        style: { lineWidth: 2,
        lineDash: [4, 8]},
        axis: { y: false },
        scale: { y: sharedYScale },
      },
    ],
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill:"#000" },
      y: { title: false, grid: true, labelFill:"#000", labelFormatter: (v) => formatWhole(v) },
    },
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {barData.length > 0 && <CustomLegend />}
      {barData.length ? (
        <div style={{ height: 350 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 350, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

function EffluentMonthlyChartIndustrial({ resultsByYear , t, resultsProductions}) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // effluentChartData.js para poder reusarla también desde el export a PDF
  // (pdfEffluentCharts.js).
  const { years, consumptionData, averageData, yearColorMap } = useMemo(
    () => buildEffluentIntensityIndustrialData(resultsByYear, resultsProductions, t),
    [resultsByYear, resultsProductions, t]
  );

  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(years);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredConsumptionData = useMemo(() =>
    consumptionData.filter(d => activeYears.includes(String(d.year))),
  [consumptionData, activeYears]);

  const filteredAverageData = useMemo(() =>
    averageData.filter(d => activeYears.includes(String(d.year))),
  [averageData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredConsumptionData, filteredAverageData);

  const config = {
    xField: "month",
    legend: false,
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    tooltip: {
      shared: true,
      items: [
        (item) => ({
          name: item.type,
          value: item.value !== null ? formatMagnitudeAdaptive(item.value) : "-",
          color: yearColorMap[item.year],
        }),
      ],
    },
    children: [
      {
        data: filteredConsumptionData,
        type: "line",
        yField: "value",
        colorField: "year",
        connectNulls: false,
        style: { lineWidth: 4 },
        scale: { y: sharedYScale },
      },
      {
        data: filteredAverageData,
        type: "line",
        yField: "value",
        colorField: "year",
        axis: { y: false },
        scale: { y: sharedYScale },
        style: { lineWidth: 2, lineDash: [4, 8] },
        connectNulls: false,
      },
    ],
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false },
      y: { title: false, grid: true, labelFormatter: (v) => formatMagnitudeAdaptive(v) },
    },
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {consumptionData.length > 0 && <CustomLegend />}
      {consumptionData.length ? (
        <div style={{ height: 450 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 450, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

function EffluentComparisonChartDomestic({ resultsByYear, t }) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // effluentChartData.js para poder reusarla también desde el export a PDF
  // (pdfEffluentCharts.js).
  const { years, barData, lineData, yearColorMap } = useMemo(
    () => buildEffluentComparisonDomesticData(resultsByYear, t),
    [resultsByYear, t]
  );

  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(years);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredBarData = useMemo(() =>
    barData.filter(d => activeYears.includes(String(d.year))),
  [barData, activeYears]);

  const filteredLineData = useMemo(() =>
    lineData.filter(d => activeYears.includes(String(d.year))),
  [lineData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredBarData, filteredLineData);

  const config = {
    xField: "month",
    legend: false,
        autoFit: true,
  padding: 'auto', // Deja que la librería calcule el espacio
  appendPadding: [20, 0, 20, 0], // [arriba, derecha, abajo, izquierda]
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    tooltip: {
      items: [
        (item) => ({
          name: item.type,
          value: formatWhole(item.value),
        }),
      ],
    },
    children: [
      {
        data: filteredBarData,
        type: "interval",
        yField: "value",
        colorField: "year",
        seriesField: "year",
        group: true,
        style: { maxWidth: 40 },
        scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
        interaction: { elementHighlight: { background: true } },
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year",
        style: { lineWidth: 2, lineDash: [4, 8] },
        axis: { y: false },
        scale: { y: sharedYScale },
      },
    ],
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false,labelFill:"#000" },
      y: { title: false, grid: true ,labelFill:"#000", labelFormatter: (v) => formatWhole(v)},
    },
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {barData.length > 0 && <CustomLegend />}
      {barData.length ? (
        <div style={{ height: 350 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 350, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

function EffluentMonthlyChartDomestic({ resultsByYear , t, resultsProductions, resultsWorkers }) {
  // Datos del gráfico — misma lógica que antes, extraída a
  // effluentChartData.js para poder reusarla también desde el export a PDF
  // (pdfEffluentCharts.js).
  const { years, consumptionData, averageData, yearColorMap } = useMemo(
    () => buildEffluentIntensityDomesticData(resultsByYear, resultsWorkers, t),
    [resultsByYear, resultsWorkers, t]
  );

  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(years);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredConsumptionData = useMemo(() =>
    consumptionData.filter(d => activeYears.includes(String(d.year))),
  [consumptionData, activeYears]);

  const filteredAverageData = useMemo(() =>
    averageData.filter(d => activeYears.includes(String(d.year))),
  [averageData, activeYears]);

  const sharedYScale = buildSharedYScale(filteredConsumptionData, filteredAverageData);

  const config = {
    xField: "month",
    legend: false,
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    tooltip: {
      shared: true,
      items: [
        (item) => ({
          name: item.type,
          value: item.value !== null ? formatMagnitudeAdaptive(item.value) : "-",
          color: yearColorMap[item.year],
        }),
      ],
    },
    children: [
      {
        data: filteredConsumptionData,
        type: "line",
        yField: "value",
        colorField: "year",
        connectNulls: false,
        style: { lineWidth: 4 },
        scale: { y: sharedYScale },
      },
      {
        data: filteredAverageData,
        type: "line",
        yField: "value",
        colorField: "year",
        axis: { y: false },
        scale: { y: sharedYScale },
        style: { lineWidth: 2, lineDash: [4, 8] },
        connectNulls: false,
      },
    ],
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false },
      y: { title: false, grid: true, labelFormatter: (v) => formatMagnitudeAdaptive(v) },
    },
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {consumptionData.length > 0 && <CustomLegend />}
      {consumptionData.length ? (
        <div style={{ height: 450 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 450, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

// Componente para mostrar valores intermedios utilizados en los cálculos
function IntermediateValuesDisplay({ resultsProductions, resultsWorkers, t }) {
  const [isOpen, setIsOpen] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Obtener todos los años disponibles y ordenarlos de mayor a menor
  const availableYears = Object.keys(resultsProductions || {}).sort(
    (a, b) => b - a
  );

  return (
    <div
      style={{
        margin: "20px 0",
        padding: "15px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #dee2e6",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "10px 0",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <h4 style={{ margin: 0, color: "#495057" }}>
          <Icon name={isOpen ? "chevron down" : "chevron right"} />
          Valores intermedios para verificación de cálculos - Últimos 5 años
        </h4>
        <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
          {isOpen ? "Haz clic para ocultar" : "Haz clic para ver detalles"}
        </span>
      </div>

      {isOpen && (
        <>
          {availableYears.map((year) => (
            <div key={year} style={{ marginBottom: "30px" }}>
              <h5
                style={{
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#495057",
                }}
              >
                Año {year}
              </h5>
              <Table celled size="small" style={{ fontSize: "0.9em" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell textAlign="center">Mes</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Producción (VPS/VFS)
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Trabajadores
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {months.map((month) => {
                    const production = resultsProductions?.[year]?.[month] || 0;
                    const workers = resultsWorkers?.[year]?.[month] || 0;

                    return (
                      <Table.Row key={month}>
                        <Table.Cell textAlign="center">
                          {t ? t(month) : month}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {production > 0 ? production.toFixed(2) : "-"}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {workers > 0 ? workers : "-"}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  <Table.Row
                    style={{ fontWeight: "bold", backgroundColor: "#e9ecef" }}
                  >
                    <Table.Cell textAlign="center">Total</Table.Cell>
                    <Table.Cell textAlign="center">
                      {Object.values(resultsProductions?.[year] || {})
                        .reduce((a, b) => a + b, 0)
                        .toFixed(2)}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {Object.values(resultsWorkers?.[year] || {}).reduce(
                        (a, b) => a + b,
                        0
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row
                    style={{ fontWeight: "bold", backgroundColor: "#d1ecf1", color: "#0c5460" }}
                  >
                    <Table.Cell textAlign="center">Promedio</Table.Cell>
                    <Table.Cell textAlign="center">
                      {(() => {
                        const values = Object.values(resultsProductions?.[year] || {});
                        const count = values.filter(v => v > 0).length;
                        const avg = count > 0 ? values.reduce((a, b) => a + b, 0) / count : 0;
                        return avg.toFixed(2);
                      })()}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {(() => {
                        const values = Object.values(resultsWorkers?.[year] || {});
                        const count = values.filter(v => v > 0).length;
                        const avg = count > 0 ? values.reduce((a, b) => a + b, 0) / count : 0;
                        return avg.toFixed(0);
                      })()}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          ))}

          <div
            style={{
              fontSize: "0.85em",
              color: "#6c757d",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            <strong>Nota:</strong> Estos valores se utilizan como denominadores
            en los cálculos de intensidad. Producción (VPS/VFS) para intensidad
            industrial y Trabajadores para intensidad doméstica.
          </div>
        </>
      )}
    </div>
  );
}
